import { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Button, Container } from "reactstrap";
import Jumbotron from "reactstrap/lib/Jumbotron";
import moment from "moment";
import { useStores } from "store/useStore";
import {
  getMfaStatus,
  MFA_STATUS_CHANGED_EVENT,
} from "store/persistence/persistMfa";
import { MFA_LOCKED_EVENT } from "domain/helpers/setAxiosDefaults";
import FullScreenMessage from "components/Billing/FullScreenMessage";
import TwoFactorSettings from "components/Profile/TwoFactorSettings";
import { MFA_GRACE_DAYS } from "components/Team/RequireMfaToggle";
import {
  SystemBanner,
  SystemBannerCta,
} from "components/SystemBanner/SystemBanner";
import { TopBannerPriority } from "components/SystemBanner/topBannerSlot";

/**
 * Shows the account's "require two-factor" rule to members who have not
 * enrolled yet. Inside the grace window they get a dismissible banner; once
 * it lapses the app is replaced with the enrolment wizard.
 *
 * The SERVER enforces the rule - every request from a locked member is
 * refused except the few that let them enrol - and it tells this component
 * the outcome in `account.mfaEnforcement`. The local grace maths below only
 * fills in for older responses; when the server says `locked`, that is final.
 * Login itself stays open on purpose: a member must be able to reach this
 * screen to enrol.
 */
const MfaEnforcementGate = observer(() => {
  const { accountStore, authStore } = useStores();
  const account = accountStore.account;
  const requireMfa = Boolean(account?.flags?.requireMfa);
  const serverEnforcement = account?.mfaEnforcement;

  // null = unknown (still fetching / not fetched)
  const [selfEnabled, setSelfEnabled] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // set when any request comes back refused with "two-factor required": the
  // server has already decided, whatever this tab loaded earlier
  const [refusedByServer, setRefusedByServer] = useState(false);
  useEffect(() => {
    const onLocked = () => setRefusedByServer(true);
    window.addEventListener(MFA_LOCKED_EVENT, onLocked);
    return () => window.removeEventListener(MFA_LOCKED_EVENT, onLocked);
  }, []);

  const check = useCallback(async () => {
    try {
      const status = await getMfaStatus();
      setSelfEnabled(status.enabled);
    } catch {
      // can't tell - never lock someone out on a failed status call
      setSelfEnabled(null);
    }
  }, []);

  useEffect(() => {
    if (requireMfa) {
      check();
    }
  }, [requireMfa, check]);

  // Enrolling from Your Profile happens in a different part of the tree, so
  // without this the banner would keep counting down at someone who has just
  // finished enrolling.
  useEffect(() => {
    const onChanged = (e: Event) => {
      const enabled = (e as CustomEvent)?.detail?.enabled;
      if (typeof enabled === "boolean") {
        setSelfEnabled(enabled);
        if (enabled) {
          // a refusal from before they enrolled is no longer the truth
          setRefusedByServer(false);
        }
      } else {
        check();
      }
    };
    window.addEventListener(MFA_STATUS_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(MFA_STATUS_CHANGED_EVENT, onChanged);
  }, [check]);

  const since = account?.flags?.requireMfaSince;
  // The server owns this stamp: PATCH /accounts/me writes it in ISO form when
  // the requirement turns on and ignores anything the client sends. So a
  // missing OR unparseable value can only come from a hand-edited row, and
  // both get the same treatment - the banner, forever, never the takeover.
  //
  // Strict ISO parsing matters here: moment("7 days") is an Invalid Date, and
  // moment().isBefore(invalid) is false - which used to skip the banner and
  // land straight on the takeover, the exact outcome this guards against.
  const stamp = since ? moment(since, moment.ISO_8601, true) : null;
  const stampUsable = Boolean(stamp && stamp.isValid());
  if (requireMfa && !stampUsable && !serverEnforcement) {
    // eslint-disable-next-line no-console
    console.warn(
      "[mfa] requireMfa is on but requireMfaSince is missing or not an ISO date; showing the banner indefinitely rather than locking anyone out. The server stamps this on write, so the row was probably edited by hand."
    );
  }
  // Prefer the server's own answer for when grace ends; fall back to the
  // local maths only for a response that predates `mfaEnforcement`.
  const serverGraceEnds = serverEnforcement?.graceEndsAt
    ? moment(serverEnforcement.graceEndsAt, moment.ISO_8601, true)
    : null;
  const graceEnds =
    serverGraceEnds && serverGraceEnds.isValid()
      ? serverGraceEnds
      : stampUsable
      ? (stamp as moment.Moment).clone().add(MFA_GRACE_DAYS, "days")
      : moment().add(MFA_GRACE_DAYS, "days");
  // locked = the server refuses this member everything but enrolment. It
  // is final regardless of what the status call said or failed to say.
  // ...except that the verdict is by definition stale once this member has
  // just enrolled: they proved a factor after the account was loaded, and
  // without this they would sit behind the takeover until a manual reload.
  const locked =
    (Boolean(serverEnforcement?.locked) || refusedByServer) &&
    selfEnabled !== true;
  const inGrace = !locked && moment().isBefore(graceEnds);
  const showBanner =
    requireMfa && selfEnabled === false && inGrace && !dismissed;

  // A locked member cannot reach the account switcher (the nav is hidden
  // behind the takeover), so offer their other accounts here. Switching is a
  // login door of its own: the target account's factor is demanded on the
  // way in, so this cannot be used to dodge a requirement.
  const otherAccounts = (account?.allAccounts || []).filter(
    (other) => other.id !== account?.accountId
  );

  if (!locked && (!requireMfa || selfEnabled !== false)) {
    return null;
  }

  if (!locked && inGrace) {
    // The strip outranks anything optional while this is up: a member days
    // from losing access is not the person to show a feature announcement to
    // instead.
    return (
      <SystemBanner
        id="mfa-enforcement"
        priority={TopBannerPriority.mfaEnforcement}
        show={showBanner}
        tone="warning"
        onDismiss={() => setDismissed(true)}
        data-testid="mfa-grace-banner"
      >
        Your account owner now requires two-factor authentication. You have{" "}
        {graceEnds.fromNow(true)} before you lose access.{" "}
        <SystemBannerCta
          href="/settings/profile"
          data-testid="mfa-banner-setup-button"
        >
          Click here to set it up.
        </SystemBannerCta>
      </SystemBanner>
    );
  }

  return (
    <FullScreenMessage className="mfa-required-message">
      <Jumbotron className="h-100 mb-0">
        <Container className="text-center" style={{ maxWidth: "560px" }}>
          <h3 className="mt-3" data-testid="mfa-takeover-title">
            Two-factor authentication is required
          </h3>
          <div className="mt-3 lead">
            Your account owner requires everyone on this team to use two-factor
            authentication. Set it up below to keep using Encharge.
          </div>
          <div className="mt-4 text-left">
            <TwoFactorSettings
              onStatusChange={(enabled) => setSelfEnabled(enabled)}
            />
          </div>
          {otherAccounts.length > 0 && (
            <div
              className="mt-4 small text-muted"
              data-testid="mfa-takeover-switcher"
            >
              Or open one of your other accounts:{" "}
              {otherAccounts.map((other) => (
                <Button
                  key={other.id}
                  color="link"
                  size="sm"
                  className="p-0 align-baseline mr-2"
                  data-testid={`mfa-switch-${other.id}`}
                  onClick={() => accountStore.switchAccount(other.id)}
                >
                  {other.name || other.site || `Account ${other.id}`}
                </Button>
              ))}
            </div>
          )}
          <div className="mt-4 small text-muted">
            Lost access to your authenticator?{" "}
            <Button
              color="link"
              size="sm"
              className="p-0 align-baseline"
              onClick={() => authStore.logOut()}
            >
              Log out
            </Button>{" "}
            and ask your account owner to reset your two-factor authentication.
          </div>
        </Container>
      </Jumbotron>
    </FullScreenMessage>
  );
});

export default MfaEnforcementGate;
